import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { collection, query, where, orderBy, onSnapshot } from 'firebase/firestore';
import { db } from '../firebase';
import { httpsCallable } from 'firebase/functions';
import { useAuth } from '../hooks/useAuth';
import { useNotifications } from '../context/NotificationContext';
import toast from 'react-hot-toast';
import { 
  ArrowLeftIcon,
  CalendarDaysIcon,
  ChevronDownIcon,
  ChevronUpIcon,
  ExclamationTriangleIcon,
  ClockIcon,
  CheckCircleIcon,
  XCircleIcon,
  MagnifyingGlassIcon,
  FunnelIcon,
  ChartBarIcon,
  XMarkIcon
} from '@heroicons/react/24/outline';

const PointsRedeem: React.FC = React.memo(() => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { addNotification } = useNotifications();
  const [pointsAmount, setPointsAmount] = useState('');
  const [pointsLoading, setPointsLoading] = useState(false);
  const [pointsErrorMessage, setPointsErrorMessage] = useState('');
  const [showPointsConfirmPopup, setShowPointsConfirmPopup] = useState(false);
  const [screenWidth, setScreenWidth] = useState(window.innerWidth);
  const [pointsWithdrawalRequests, setPointsWithdrawalRequests] = useState<any[]>([]);
  const [showPointsHistory, setShowPointsHistory] = useState(false);
  const [openPolicyDropdown, setOpenPolicyDropdown] = useState<'redemption' | 'delivery' | 'gifting' | null>(null);
  const [historyFilter, setHistoryFilter] = useState<'all' | 'pending' | 'approved' | 'rejected'>('all');
  const [historySearch, setHistorySearch] = useState('');
  const [sortBy, setSortBy] = useState<'date' | 'points' | 'status'>('date');

  // Track screen width for responsive design
  useEffect(() => {
    const handleResize = () => {
      setScreenWidth(window.innerWidth);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Fetch points withdrawal requests
  useEffect(() => {
    if (!user) {
      setPointsWithdrawalRequests([]);
      return;
    }

    let unsubscribe: (() => void) | null = null;

    const setupListener = () => {
      try {
        const pointsQuery = query(
          collection(db, 'pointsWithdrawalRequests'),
          where('userId', '==', user.id),
          orderBy('createdAt', 'desc')
        );

        unsubscribe = onSnapshot(
          pointsQuery,
          (snapshot) => {
            const requests = snapshot.docs.map(doc => {
              const data = doc.data();
              return {
                id: doc.id,
                ...data,
                points: data.points || 0,
                status: data.status || 'pending',
                createdAt: data.createdAt,
                processedAt: data.processedAt,
                rejectedAt: data.rejectedAt,
                rejectReason: data.rejectReason
              };
            });
            
            console.log('✅ Points withdrawal requests updated:', requests.length, requests);
            setPointsWithdrawalRequests(requests);
          },
          (error) => {
            console.error('❌ Error fetching points withdrawal requests:', error);
            // If orderBy fails due to missing index, try without it
            if (error.code === 'failed-precondition') {
              console.log('⚠️ Index missing, trying query without orderBy...');
              const simpleQuery = query(
                collection(db, 'pointsWithdrawalRequests'),
                where('userId', '==', user.id)
              );
              
              const fallbackUnsubscribe = onSnapshot(
                simpleQuery,
                (snapshot) => {
                  const requests = snapshot.docs.map(doc => {
                    const data = doc.data();
                    return {
                      id: doc.id,
                      ...data,
                      points: data.points || 0,
                      status: data.status || 'pending',
                      createdAt: data.createdAt,
                      processedAt: data.processedAt,
                      rejectedAt: data.rejectedAt,
                      rejectReason: data.rejectReason
                    };
                  });
                  // Sort manually by createdAt descending
                  requests.sort((a, b) => {
                    const dateA = a.createdAt?.toDate ? a.createdAt.toDate() : new Date(a.createdAt || 0);
                    const dateB = b.createdAt?.toDate ? b.createdAt.toDate() : new Date(b.createdAt || 0);
                    return dateB.getTime() - dateA.getTime();
                  });
                  console.log('✅ Points withdrawal requests fetched (fallback):', requests.length, requests);
                  setPointsWithdrawalRequests(requests);
                },
                (fallbackError) => {
                  console.error('❌ Fallback query also failed:', fallbackError);
                  setPointsWithdrawalRequests([]);
                }
              );
              
              unsubscribe = fallbackUnsubscribe;
            } else {
              setPointsWithdrawalRequests([]);
            }
          }
        );
      } catch (error) {
        console.error('❌ Error setting up points withdrawal listener:', error);
        setPointsWithdrawalRequests([]);
      }
    };

    setupListener();

    return () => {
      if (unsubscribe) {
        unsubscribe();
      }
    };
  }, [user]);

  // Calculate available points (subtract pending points withdrawal requests)
  const rewardPoints = useMemo(() => {
    const currentPoints = user?.pointsBalance || 0;
    const pendingPointsRequests = pointsWithdrawalRequests.filter(req => req.status === 'pending');
    const totalPendingPoints = pendingPointsRequests.reduce((sum, req) => sum + (req.points || 0), 0);
    const available = Math.max(0, currentPoints - totalPendingPoints);
    return available;
  }, [user?.pointsBalance, pointsWithdrawalRequests]);

  // Calculate history statistics
  const historyStats = useMemo(() => {
    const total = pointsWithdrawalRequests.length;
    const pending = pointsWithdrawalRequests.filter(r => r.status === 'pending').length;
    const approved = pointsWithdrawalRequests.filter(r => r.status === 'approved').length;
    const rejected = pointsWithdrawalRequests.filter(r => r.status === 'rejected').length;
    const totalPoints = pointsWithdrawalRequests.reduce((sum, r) => sum + (r.points || 0), 0);
    const approvedPoints = pointsWithdrawalRequests.filter(r => r.status === 'approved').reduce((sum, r) => sum + (r.points || 0), 0);
    return { total, pending, approved, rejected, totalPoints, approvedPoints };
  }, [pointsWithdrawalRequests]);

  // Filter and sort history
  const filteredHistory = useMemo(() => {
    let filtered = pointsWithdrawalRequests;

    // Filter by status
    if (historyFilter !== 'all') {
      filtered = filtered.filter(r => r.status === historyFilter);
    }

    // Filter by search (points amount or request ID)
    if (historySearch.trim()) {
      const searchLower = historySearch.toLowerCase().trim();
      filtered = filtered.filter(r => {
        const pointsStr = (r.points || 0).toString();
        const idStr = (r.id || '').toLowerCase();
        return pointsStr.includes(searchLower) || idStr.includes(searchLower);
      });
    }

    // Sort
    filtered = [...filtered].sort((a, b) => {
      if (sortBy === 'date') {
        const dateA = a.createdAt?.toDate ? a.createdAt.toDate() : new Date(a.createdAt || 0);
        const dateB = b.createdAt?.toDate ? b.createdAt.toDate() : new Date(b.createdAt || 0);
        return dateB.getTime() - dateA.getTime();
      } else if (sortBy === 'points') {
        return (b.points || 0) - (a.points || 0);
      } else {
        // Sort by status: pending first, then approved, then rejected
        const statusOrder = { pending: 0, approved: 1, rejected: 2 };
        return (statusOrder[a.status as keyof typeof statusOrder] || 3) - (statusOrder[b.status as keyof typeof statusOrder] || 3);
      }
    });

    return filtered;
  }, [pointsWithdrawalRequests, historyFilter, historySearch, sortBy]);

  const handlePointsWithdrawal = () => {
    if (!user) return;

    // Check if dealer is added
    if (!user.dealerCode || !user.dealerId) {
      setPointsErrorMessage('Please add a dealer in your Profile to withdraw points. Click "Go to Profile" below.');
      return;
    }

    const pointsValue = parseInt(pointsAmount);
    
    // Validation
    if (!pointsAmount || isNaN(pointsValue) || pointsValue < 500) {
      setPointsErrorMessage('Minimum withdrawal is 500 points');
      return;
    }

    if (pointsValue > rewardPoints) {
      setPointsErrorMessage(`You only have ${rewardPoints} points available`);
      return;
    }

    // Check if it's a valid increment (500, 600, 700, etc.)
    if ((pointsValue - 500) % 100 !== 0) {
      setPointsErrorMessage('Points must be in increments of 100 starting from 500 (500, 600, 700, etc.)');
      return;
    }

    // Show confirmation popup
    setShowPointsConfirmPopup(true);
  };

  return (
    <div style={{
      minHeight: '100vh',
      background: 'linear-gradient(135deg, #ffffff 0%, #fff7ed 50%, #f8fafc 100%)',
      paddingBottom: 'calc(72px + env(safe-area-inset-bottom))'
    }}>
      {/* Fixed Header with Back Button */}
      <div style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        zIndex: 1000,
        background: 'rgba(255, 255, 255, 0.98)',
        backdropFilter: 'blur(24px) saturate(180%)',
        borderBottom: '1px solid rgba(0, 0, 0, 0.08)',
        padding: '16px 20px',
        display: 'flex',
        alignItems: 'center',
        gap: '16px',
        boxShadow: '0 2px 8px rgba(0, 0, 0, 0.05)'
      }}>
        <button
          onClick={() => navigate('/payment')}
          style={{
            background: 'transparent',
            border: 'none',
            cursor: 'pointer',
            padding: '8px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            borderRadius: '8px',
            transition: 'all 0.2s ease'
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.backgroundColor = '#f3f4f6';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.backgroundColor = 'transparent';
          }}
        >
          <ArrowLeftIcon style={{ width: '24px', height: '24px', color: '#1f2937' }} />
        </button>
        <h1 style={{
          fontSize: '20px',
          fontWeight: '900',
          color: '#111827',
          margin: 0,
          flex: 1
        }}>
          Points Redemption
        </h1>
      </div>

      {/* Content - with top padding for fixed header */}
      <div style={{
        padding: screenWidth < 360 ? '20px' : '24px',
        paddingTop: screenWidth < 360 ? 'calc(20px + 64px)' : 'calc(24px + 64px)',
        maxWidth: '600px',
        margin: '0 auto',
        width: '100%'
      }}>
        {/* Header */}
        <div style={{ textAlign: 'center', marginBottom: '24px', marginTop: '0' }}>
          <div style={{
            width: screenWidth < 360 ? '56px' : '64px',
            height: screenWidth < 360 ? '56px' : '64px',
            borderRadius: '50%',
            background: 'linear-gradient(135deg, #f97316 0%, #fb923c 100%)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            margin: '0 auto 12px',
            boxShadow: '0 4px 12px rgba(249, 115, 22, 0.3)'
          }}>
            <span style={{ fontSize: screenWidth < 360 ? '28px' : '32px' }}>🎁</span>
          </div>
          <h2 style={{
            fontSize: screenWidth < 360 ? '20px' : '24px',
            fontWeight: '900',
            color: '#111827',
            marginBottom: '4px'
          }}>
            Points Withdrawal
          </h2>
          <p style={{
            fontSize: screenWidth < 360 ? '12px' : '14px',
            color: '#6b7280'
          }}>
            Withdraw your reward points
          </p>
        </div>

        {/* Current Points Balance */}
        <div style={{
          background: 'linear-gradient(135deg, #f97316 0%, #fb923c 50%, #fdba74 100%)',
          borderRadius: '20px',
          padding: '20px',
          marginBottom: '24px',
          color: 'white',
          textAlign: 'center',
          boxShadow: '0 8px 24px rgba(249, 115, 22, 0.4), inset 0 1px 0 rgba(255, 255, 255, 0.2)',
          position: 'relative',
          overflow: 'hidden'
        }}>
          {/* Decorative circles */}
          <div style={{
            position: 'absolute',
            top: '-20px',
            right: '-20px',
            width: '80px',
            height: '80px',
            borderRadius: '50%',
            background: 'rgba(255, 255, 255, 0.1)',
            opacity: 0.5
          }} />
          <div style={{
            position: 'absolute',
            bottom: '-30px',
            left: '-30px',
            width: '100px',
            height: '100px',
            borderRadius: '50%',
            background: 'rgba(255, 255, 255, 0.08)',
            opacity: 0.5
          }} />
          
          <div style={{
            fontSize: '13px',
            fontWeight: '700',
            opacity: 0.95,
            marginBottom: '8px',
            textTransform: 'uppercase',
            letterSpacing: '0.5px',
            position: 'relative',
            zIndex: 1
          }}>
            Available Points
          </div>
          <div style={{
            fontSize: '40px',
            fontWeight: '900',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '6px',
            position: 'relative',
            zIndex: 1,
            textShadow: '0 2px 8px rgba(0, 0, 0, 0.2)'
          }}>
            <span style={{ fontSize: '32px' }}>₽</span>
            <span>{rewardPoints.toLocaleString('en-IN')}</span>
          </div>
          {pointsWithdrawalRequests.filter(r => r.status === 'pending').length > 0 && (
            <div style={{
              marginTop: '12px',
              padding: '8px 12px',
              backgroundColor: 'rgba(255, 255, 255, 0.2)',
              borderRadius: '12px',
              fontSize: '12px',
              fontWeight: '600',
              position: 'relative',
              zIndex: 1,
              backdropFilter: 'blur(10px)'
            }}>
              {pointsWithdrawalRequests.filter(r => r.status === 'pending').length} pending request{pointsWithdrawalRequests.filter(r => r.status === 'pending').length > 1 ? 's' : ''}
            </div>
          )}
        </div>

        {/* Points Withdrawal Form */}
        <form onSubmit={(e) => {
          e.preventDefault();
          handlePointsWithdrawal();
        }}>
          <div style={{ marginBottom: '16px' }}>
            <label style={{
              fontSize: '14px',
              fontWeight: '700',
              color: '#1f2937',
              marginBottom: '8px',
              display: 'block'
            }}>
              Select Points to Withdraw
            </label>
            
            {/* Quick Select Buttons */}
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(3, 1fr)',
              gap: '10px',
              marginBottom: '16px'
            }}>
              {[500, 600, 700, 800, 900, 1000].map((points) => {
                const isValidAmount = pointsAmount && parseInt(pointsAmount) === points;
                const canSelect = rewardPoints >= points;
                return (
                  <button
                    key={points}
                    type="button"
                    onClick={() => {
                      if (canSelect) {
                        setPointsAmount(points.toString());
                        setPointsErrorMessage('');
                      }
                    }}
                    disabled={!canSelect}
                    style={{
                      padding: '14px 12px',
                      borderRadius: '14px',
                      border: isValidAmount ? '2px solid #f97316' : '2px solid #e5e7eb',
                      background: isValidAmount
                        ? 'linear-gradient(135deg, #f97316 0%, #fb923c 100%)'
                        : canSelect
                        ? 'linear-gradient(135deg, #ffffff 0%, #f9fafb 100%)'
                        : '#f3f4f6',
                      color: isValidAmount
                        ? 'white'
                        : canSelect
                        ? '#1f2937'
                        : '#9ca3af',
                      fontSize: '15px',
                      fontWeight: '800',
                      cursor: canSelect ? 'pointer' : 'not-allowed',
                      transition: 'all 0.2s ease',
                      opacity: canSelect ? 1 : 0.5,
                      boxShadow: isValidAmount 
                        ? '0 4px 12px rgba(249, 115, 22, 0.3)' 
                        : canSelect 
                        ? '0 2px 4px rgba(0, 0, 0, 0.05)' 
                        : 'none'
                    }}
                    onMouseEnter={(e) => {
                      if (canSelect && !isValidAmount) {
                        e.currentTarget.style.transform = 'translateY(-2px)';
                        e.currentTarget.style.boxShadow = '0 4px 8px rgba(0, 0, 0, 0.1)';
                        e.currentTarget.style.borderColor = '#f97316';
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (canSelect && !isValidAmount) {
                        e.currentTarget.style.transform = 'translateY(0)';
                        e.currentTarget.style.boxShadow = '0 2px 4px rgba(0, 0, 0, 0.05)';
                        e.currentTarget.style.borderColor = '#e5e7eb';
                      }
                    }}
                  >
                    ₽{points}
                  </button>
                );
              })}
            </div>

            {/* Custom Amount Input */}
            <div style={{
              display: 'flex',
              alignItems: 'center',
              background: 'linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%)',
              borderRadius: '14px',
              border: '2px solid #e9ecef',
              padding: '6px',
              transition: 'all 0.2s ease',
              boxShadow: '0 2px 4px rgba(0, 0, 0, 0.05)'
            }}
            onFocus={(e) => {
              e.currentTarget.style.borderColor = '#f97316';
              e.currentTarget.style.boxShadow = '0 4px 12px rgba(249, 115, 22, 0.15)';
            }}
            onBlur={(e) => {
              e.currentTarget.style.borderColor = '#e9ecef';
              e.currentTarget.style.boxShadow = '0 2px 4px rgba(0, 0, 0, 0.05)';
            }}
            >
              <span style={{
                fontSize: '18px',
                fontWeight: '600',
                color: '#666',
                padding: '0 12px'
              }}>₽</span>
              <input
                type="number"
                value={pointsAmount}
                onChange={(e) => {
                  const value = e.target.value;
                  if (value === '') {
                    setPointsAmount('');
                    setPointsErrorMessage('');
                    return;
                  }
                  const numValue = parseInt(value);
                  if (numValue >= 500 && numValue <= rewardPoints) {
                    // Check if it's a valid increment (500, 600, 700, etc.)
                    if ((numValue - 500) % 100 === 0) {
                      setPointsAmount(value);
                      setPointsErrorMessage('');
                    } else {
                      setPointsAmount(value);
                      setPointsErrorMessage('Points must be in increments of 100 starting from 500 (500, 600, 700, etc.)');
                    }
                  } else if (numValue < 500) {
                    setPointsAmount(value);
                    setPointsErrorMessage('Minimum withdrawal is 500 points');
                  } else if (numValue > rewardPoints) {
                    setPointsAmount(value);
                    setPointsErrorMessage(`You only have ${rewardPoints} points available`);
                  } else {
                    setPointsAmount(value);
                    setPointsErrorMessage('');
                  }
                }}
                placeholder="Enter points (500, 600, 700...)"
                min="500"
                max={rewardPoints}
                step="100"
                style={{
                  flex: 1,
                  border: 'none',
                  background: 'transparent',
                  fontSize: '18px',
                  fontWeight: '600',
                  padding: '12px',
                  outline: 'none',
                  color: '#1a1a1a'
                }}
              />
            </div>
            <div style={{
              fontSize: '12px',
              color: '#666',
              marginTop: '6px',
              marginBottom: '4px'
            }}>
              Minimum: ₽500 | Increments of 100 | Max: ₽{rewardPoints}
            </div>

            {/* Error Message */}
            {pointsErrorMessage && (
              <div style={{
                backgroundColor: '#fee2e2',
                border: '1px solid #fca5a5',
                borderRadius: '8px',
                padding: '12px',
                fontSize: '13px',
                color: '#dc2626',
                fontWeight: '600',
                marginTop: '8px',
                display: 'flex',
                flexDirection: 'column',
                gap: '10px'
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <ExclamationTriangleIcon style={{ width: '18px', height: '18px', flexShrink: 0 }} />
                  <span>{pointsErrorMessage}</span>
                </div>
                {pointsErrorMessage.includes('add a dealer') && (
                  <button
                    type="button"
                    onClick={() => {
                      navigate('/profile');
                    }}
                    style={{
                      backgroundColor: '#f97316',
                      color: 'white',
                      border: 'none',
                      borderRadius: '8px',
                      padding: '10px 16px',
                      fontSize: '13px',
                      fontWeight: '700',
                      cursor: 'pointer',
                      transition: 'all 0.2s ease',
                      alignSelf: 'flex-start',
                      boxShadow: '0 2px 4px rgba(249, 115, 22, 0.3)'
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.backgroundColor = '#ea580c';
                      e.currentTarget.style.transform = 'translateY(-1px)';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.backgroundColor = '#f97316';
                      e.currentTarget.style.transform = 'translateY(0)';
                    }}
                  >
                    Go to Profile
                  </button>
                )}
              </div>
            )}
          </div>

          {/* Submit Button */}
          <button
            type="submit"
            disabled={pointsLoading || !pointsAmount || parseInt(pointsAmount) < 500 || parseInt(pointsAmount) > rewardPoints || (parseInt(pointsAmount) - 500) % 100 !== 0 || !user?.dealerCode || !user?.dealerId}
            style={{
              width: '100%',
              background: pointsLoading || !pointsAmount || parseInt(pointsAmount) < 500 || parseInt(pointsAmount) > rewardPoints || (parseInt(pointsAmount) - 500) % 100 !== 0 || !user?.dealerCode || !user?.dealerId
                ? '#d1d5db'
                : 'linear-gradient(135deg, #f97316 0%, #fb923c 100%)',
              color: 'white',
              border: 'none',
              borderRadius: '16px',
              padding: '16px',
              fontSize: '16px',
              fontWeight: '800',
              cursor: pointsLoading || !pointsAmount || parseInt(pointsAmount) < 500 || parseInt(pointsAmount) > rewardPoints || (parseInt(pointsAmount) - 500) % 100 !== 0
                ? 'not-allowed'
                : 'pointer',
              transition: 'all 0.2s ease',
              boxShadow: pointsLoading || !pointsAmount || parseInt(pointsAmount) < 500 || parseInt(pointsAmount) > rewardPoints || (parseInt(pointsAmount) - 500) % 100 !== 0
                ? 'none'
                : '0 4px 12px rgba(249, 115, 22, 0.4)'
            }}
          >
            {pointsLoading ? 'Processing...' : (!user?.dealerCode || !user?.dealerId) ? 'Add Dealer First' : 'Redeem Points'}
          </button>
        </form>

        {/* View History Button */}
        <button
          type="button"
          onClick={() => setShowPointsHistory(true)}
          style={{
            width: '100%',
            marginTop: '16px',
            padding: '14px',
            backgroundColor: '#ffffff',
            border: '2px solid #f97316',
            borderRadius: '16px',
            fontSize: '15px',
            fontWeight: '700',
            color: '#f97316',
            cursor: 'pointer',
            transition: 'all 0.2s ease',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '8px',
            boxShadow: '0 2px 8px rgba(249, 115, 22, 0.15)'
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.backgroundColor = '#fff7ed';
            e.currentTarget.style.transform = 'translateY(-2px)';
            e.currentTarget.style.boxShadow = '0 4px 12px rgba(249, 115, 22, 0.25)';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.backgroundColor = '#ffffff';
            e.currentTarget.style.transform = 'translateY(0)';
            e.currentTarget.style.boxShadow = '0 2px 8px rgba(249, 115, 22, 0.15)';
          }}
        >
          <CalendarDaysIcon style={{ width: '20px', height: '20px' }} />
          View Withdrawal History
        </button>

        {/* Info Note */}
        <div style={{
          marginTop: '16px',
          padding: '12px',
          backgroundColor: '#fef3c7',
          borderRadius: '12px',
          fontSize: '12px',
          color: '#92400e',
          lineHeight: '1.6'
        }}>
          <strong>Note:</strong> Points withdrawal requests will be processed by admin. You can only withdraw in increments of 100 starting from 500 points.
        </div>

        {/* Policy Dropdowns */}
        <div style={{ marginTop: '20px', marginBottom: '40px', display: 'flex', flexDirection: 'column', gap: '12px' }}>
          {/* Points Redemption Policy */}
          <div style={{
            border: '1px solid #e5e7eb',
            borderRadius: '12px',
            overflow: 'hidden',
            backgroundColor: '#ffffff'
          }}>
            <button
              type="button"
              onClick={() => setOpenPolicyDropdown(openPolicyDropdown === 'redemption' ? null : 'redemption')}
              style={{
                width: '100%',
                padding: '14px 16px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                backgroundColor: '#f9fafb',
                border: 'none',
                cursor: 'pointer',
                transition: 'background-color 0.2s ease'
              }}
              onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f3f4f6'}
              onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#f9fafb'}
            >
              <span style={{ fontSize: '14px', fontWeight: '700', color: '#1f2937' }}>
                Points Redemption Policy
              </span>
              {openPolicyDropdown === 'redemption' ? (
                <ChevronUpIcon style={{ width: '20px', height: '20px', color: '#6b7280' }} />
              ) : (
                <ChevronDownIcon style={{ width: '20px', height: '20px', color: '#6b7280' }} />
              )}
            </button>
            {openPolicyDropdown === 'redemption' && (
              <div style={{
                padding: '16px',
                fontSize: '12px',
                color: '#374151',
                lineHeight: '1.8',
                backgroundColor: '#ffffff'
              }}>
                <ol style={{ margin: 0, paddingLeft: '20px', display: 'flex', flexDirection: 'column', gap: '12px' }}>
                  <li>Points can be redeemed only in multiples of 100.</li>
                  <li>Points can be redeemed once in a calendar month.</li>
                  <li>No charges or deductions will be applied during point redemption.</li>
                  <li>Points are not convertible into cash or any monetary form. Rewards will be provided only in the form of gifts.</li>
                  <li>Points are non-transferable and can be used only by the registered painter.</li>
                </ol>
              </div>
            )}
          </div>

          {/* Delivery Policy */}
          <div style={{
            border: '1px solid #e5e7eb',
            borderRadius: '12px',
            overflow: 'hidden',
            backgroundColor: '#ffffff'
          }}>
            <button
              type="button"
              onClick={() => setOpenPolicyDropdown(openPolicyDropdown === 'delivery' ? null : 'delivery')}
              style={{
                width: '100%',
                padding: '14px 16px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                backgroundColor: '#f9fafb',
                border: 'none',
                cursor: 'pointer',
                transition: 'background-color 0.2s ease'
              }}
              onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f3f4f6'}
              onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#f9fafb'}
            >
              <span style={{ fontSize: '14px', fontWeight: '700', color: '#1f2937' }}>
                Delivery Policy
              </span>
              {openPolicyDropdown === 'delivery' ? (
                <ChevronUpIcon style={{ width: '20px', height: '20px', color: '#6b7280' }} />
              ) : (
                <ChevronDownIcon style={{ width: '20px', height: '20px', color: '#6b7280' }} />
              )}
            </button>
            {openPolicyDropdown === 'delivery' && (
              <div style={{
                padding: '16px',
                fontSize: '12px',
                color: '#374151',
                lineHeight: '1.8',
                backgroundColor: '#ffffff'
              }}>
                <ol style={{ margin: 0, paddingLeft: '20px', display: 'flex', flexDirection: 'column', gap: '12px' }}>
                  <li>
                    The delivery process of the gift will be decided by the company.
                    <ul style={{ marginTop: '6px', paddingLeft: '20px', listStyleType: 'disc' }}>
                      <li>Company representatives may contact the painter/dealer for delivery-related coordination.</li>
                    </ul>
                  </li>
                  <li>The company does not guarantee any fixed delivery date or time for gift delivery.</li>
                  <li>Delivery timelines may vary depending on location, availability of products, courier service, or other logistical factors.</li>
                  <li>Painters are required to cooperate and allow time for successful delivery of the gift.</li>
                  <li>The company shall not be responsible for delays or failures caused due to force majeure events, including natural disasters, transport issues or government restrictions.</li>
                </ol>
              </div>
            )}
          </div>

          {/* Gifting Policy */}
          <div style={{
            border: '1px solid #e5e7eb',
            borderRadius: '12px',
            overflow: 'hidden',
            backgroundColor: '#ffffff'
          }}>
            <button
              type="button"
              onClick={() => setOpenPolicyDropdown(openPolicyDropdown === 'gifting' ? null : 'gifting')}
              style={{
                width: '100%',
                padding: '14px 16px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                backgroundColor: '#f9fafb',
                border: 'none',
                cursor: 'pointer',
                transition: 'background-color 0.2s ease'
              }}
              onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f3f4f6'}
              onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#f9fafb'}
            >
              <span style={{ fontSize: '14px', fontWeight: '700', color: '#1f2937' }}>
                Gifting Policy
              </span>
              {openPolicyDropdown === 'gifting' ? (
                <ChevronUpIcon style={{ width: '20px', height: '20px', color: '#6b7280' }} />
              ) : (
                <ChevronDownIcon style={{ width: '20px', height: '20px', color: '#6b7280' }} />
              )}
            </button>
            {openPolicyDropdown === 'gifting' && (
              <div style={{
                padding: '16px',
                fontSize: '12px',
                color: '#374151',
                lineHeight: '1.8',
                backgroundColor: '#ffffff'
              }}>
                <ol style={{ margin: 0, paddingLeft: '20px', display: 'flex', flexDirection: 'column', gap: '12px' }}>
                  <li>
                    The gift item, brand, model, colour, or specification shall be solely decided by the company.
                    <ul style={{ marginTop: '6px', paddingLeft: '20px', listStyleType: 'disc' }}>
                      <li>The gift may include (but is not limited to) electronics, household items, kitchenware, packaged food items, cleaning products, etc.</li>
                    </ul>
                  </li>
                  <li>
                    After receiving the gift, the painter must report any issue within 3 days of delivery.
                    <br />
                    All reported issues will be reviewed and validated solely by the company, and the company's decision regarding the nature and validity of the issue shall be final and binding.
                  </li>
                  <li>
                    For raising any dispute, the painter may:
                    <ul style={{ marginTop: '6px', paddingLeft: '20px', listStyleType: 'disc' }}>
                      <li>Contact the registered dealer, or</li>
                      <li>Email the company through the official communication channel.</li>
                    </ul>
                  </li>
                  <li>
                    To raise a valid complaint, the painter must provide proper proof, including:
                    <ul style={{ marginTop: '6px', paddingLeft: '20px', listStyleType: 'disc' }}>
                      <li>Photographs taken at the time of receiving the gift</li>
                      <li>A complete unboxing video</li>
                    </ul>
                    This is mandatory to ensure transparency and prevent misuse or false claims.
                  </li>
                  <li>Painters are required to cooperate with company officials whenever required to ensure smooth processing.</li>
                  <li>
                    The company reserves the full right to take legal action in case of:
                    <ul style={{ marginTop: '6px', paddingLeft: '20px', listStyleType: 'disc' }}>
                      <li>Fraudulent activities</li>
                      <li>Misuse or manipulation of the loyalty program</li>
                      <li>Submission of false information or evidence</li>
                    </ul>
                  </li>
                </ol>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Points Withdrawal History Modal */}
      {showPointsHistory && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.85)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 10001,
          padding: screenWidth < 360 ? '12px' : '20px',
          overflowY: 'auto'
        }}
        onClick={() => setShowPointsHistory(false)}
        >
          <div style={{
            background: 'linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)',
            borderRadius: screenWidth < 360 ? '20px' : '24px',
            padding: screenWidth < 360 ? '20px' : '28px',
            maxWidth: screenWidth < 360 ? 'calc(100vw - 24px)' : 'min(500px, calc(100vw - 32px))',
            width: '100%',
            boxShadow: '0 20px 60px rgba(0, 0, 0, 0.4)',
            animation: 'slideIn 0.3s ease-out',
            maxHeight: 'calc(95vh - 40px)',
            overflowY: 'auto',
            position: 'relative'
          }}
          onClick={(e) => e.stopPropagation()}
          >
            {/* Close Button */}
            <button
              onClick={() => setShowPointsHistory(false)}
              style={{
                position: 'absolute',
                top: '16px',
                right: '16px',
                background: 'transparent',
                border: 'none',
                cursor: 'pointer',
                padding: '8px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                borderRadius: '8px',
                color: '#6b7280',
                transition: 'all 0.2s ease'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = '#f3f4f6';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = 'transparent';
              }}
            >
              <XCircleIcon style={{ width: '24px', height: '24px' }} />
            </button>

            {/* Header */}
            <div style={{ textAlign: 'center', marginBottom: '20px', marginTop: '8px' }}>
              <div style={{
                width: screenWidth < 360 ? '56px' : '64px',
                height: screenWidth < 360 ? '56px' : '64px',
                borderRadius: '50%',
                background: 'linear-gradient(135deg, #f97316 0%, #fb923c 100%)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                margin: '0 auto 12px',
                boxShadow: '0 4px 12px rgba(249, 115, 22, 0.3)'
              }}>
                <ChartBarIcon style={{ width: screenWidth < 360 ? '28px' : '32px', height: screenWidth < 360 ? '28px' : '32px', color: 'white' }} />
              </div>
              <h2 style={{
                fontSize: screenWidth < 360 ? '20px' : '24px',
                fontWeight: '900',
                color: '#111827',
                marginBottom: '4px'
              }}>
                Points Withdrawal History
              </h2>
              <p style={{
                fontSize: screenWidth < 360 ? '12px' : '14px',
                color: '#6b7280'
              }}>
                {historyStats.total} total request{historyStats.total !== 1 ? 's' : ''}
              </p>
            </div>

            {/* Statistics Cards */}
            {pointsWithdrawalRequests.length > 0 && (
              <div style={{
                display: 'grid',
                gridTemplateColumns: screenWidth < 360 ? 'repeat(2, 1fr)' : 'repeat(4, 1fr)',
                gap: '10px',
                marginBottom: '20px'
              }}>
                <div style={{
                  background: 'linear-gradient(135deg, #fef3c7 0%, #fde68a 100%)',
                  borderRadius: '12px',
                  padding: '12px',
                  textAlign: 'center',
                  border: '1px solid #fbbf24'
                }}>
                  <div style={{ fontSize: '20px', fontWeight: '900', color: '#92400e', marginBottom: '4px' }}>
                    {historyStats.pending}
                  </div>
                  <div style={{ fontSize: '10px', fontWeight: '700', color: '#78350f', textTransform: 'uppercase' }}>
                    Pending
                  </div>
                </div>
                <div style={{
                  background: 'linear-gradient(135deg, #d1fae5 0%, #a7f3d0 100%)',
                  borderRadius: '12px',
                  padding: '12px',
                  textAlign: 'center',
                  border: '1px solid #34d399'
                }}>
                  <div style={{ fontSize: '20px', fontWeight: '900', color: '#065f46', marginBottom: '4px' }}>
                    {historyStats.approved}
                  </div>
                  <div style={{ fontSize: '10px', fontWeight: '700', color: '#064e3b', textTransform: 'uppercase' }}>
                    Approved
                  </div>
                </div>
                <div style={{
                  background: 'linear-gradient(135deg, #fee2e2 0%, #fecaca 100%)',
                  borderRadius: '12px',
                  padding: '12px',
                  textAlign: 'center',
                  border: '1px solid #f87171'
                }}>
                  <div style={{ fontSize: '20px', fontWeight: '900', color: '#991b1b', marginBottom: '4px' }}>
                    {historyStats.rejected}
                  </div>
                  <div style={{ fontSize: '10px', fontWeight: '700', color: '#7f1d1d', textTransform: 'uppercase' }}>
                    Rejected
                  </div>
                </div>
                <div style={{
                  background: 'linear-gradient(135deg, #f97316 0%, #fb923c 100%)',
                  borderRadius: '12px',
                  padding: '12px',
                  textAlign: 'center',
                  border: '1px solid #f97316',
                  color: 'white'
                }}>
                  <div style={{ fontSize: '20px', fontWeight: '900', marginBottom: '4px' }}>
                    ₽{historyStats.approvedPoints.toLocaleString('en-IN')}
                  </div>
                  <div style={{ fontSize: '10px', fontWeight: '700', textTransform: 'uppercase', opacity: 0.9 }}>
                    Redeemed
                  </div>
                </div>
              </div>
            )}

            {/* Search and Filter Bar */}
            <div style={{
              display: 'flex',
              flexDirection: 'column',
              gap: '12px',
              marginBottom: '20px'
            }}>
              {/* Search Bar */}
              <div style={{
                position: 'relative',
                display: 'flex',
                alignItems: 'center'
              }}>
                <MagnifyingGlassIcon style={{
                  position: 'absolute',
                  left: '12px',
                  width: '18px',
                  height: '18px',
                  color: '#9ca3af'
                }} />
                <input
                  type="text"
                  placeholder="Search by points or ID..."
                  value={historySearch}
                  onChange={(e) => setHistorySearch(e.target.value)}
                  style={{
                    width: '100%',
                    padding: '12px 12px 12px 40px',
                    borderRadius: '12px',
                    border: '2px solid #e5e7eb',
                    fontSize: '14px',
                    outline: 'none',
                    transition: 'all 0.2s ease',
                    backgroundColor: '#ffffff'
                  }}
                  onFocus={(e) => {
                    e.currentTarget.style.borderColor = '#f97316';
                    e.currentTarget.style.boxShadow = '0 0 0 3px rgba(249, 115, 22, 0.1)';
                  }}
                  onBlur={(e) => {
                    e.currentTarget.style.borderColor = '#e5e7eb';
                    e.currentTarget.style.boxShadow = 'none';
                  }}
                />
                {historySearch && (
                  <button
                    onClick={() => setHistorySearch('')}
                    style={{
                      position: 'absolute',
                      right: '8px',
                      background: 'transparent',
                      border: 'none',
                      cursor: 'pointer',
                      padding: '4px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      borderRadius: '4px',
                      color: '#6b7280'
                    }}
                  >
                    <XMarkIcon style={{ width: '18px', height: '18px' }} />
                  </button>
                )}
              </div>

              {/* Filter and Sort Row */}
              <div style={{
                display: 'flex',
                gap: '8px',
                flexWrap: 'wrap'
              }}>
                {/* Filter Buttons */}
                <div style={{
                  display: 'flex',
                  gap: '6px',
                  flex: 1,
                  minWidth: '200px'
                }}>
                  {(['all', 'pending', 'approved', 'rejected'] as const).map((filter) => (
                    <button
                      key={filter}
                      onClick={() => setHistoryFilter(filter)}
                      style={{
                        flex: 1,
                        padding: '8px 12px',
                        borderRadius: '10px',
                        border: '2px solid',
                        borderColor: historyFilter === filter ? '#f97316' : '#e5e7eb',
                        background: historyFilter === filter
                          ? 'linear-gradient(135deg, #f97316 0%, #fb923c 100%)'
                          : '#ffffff',
                        color: historyFilter === filter ? 'white' : '#6b7280',
                        fontSize: '11px',
                        fontWeight: '700',
                        textTransform: 'capitalize',
                        cursor: 'pointer',
                        transition: 'all 0.2s ease',
                        boxShadow: historyFilter === filter ? '0 2px 8px rgba(249, 115, 22, 0.3)' : 'none'
                      }}
                    >
                      {filter}
                    </button>
                  ))}
                </div>

                {/* Sort Dropdown */}
                <div style={{ position: 'relative' }}>
                  <select
                    value={sortBy}
                    onChange={(e) => setSortBy(e.target.value as 'date' | 'points' | 'status')}
                    style={{
                      padding: '8px 32px 8px 12px',
                      borderRadius: '10px',
                      border: '2px solid #e5e7eb',
                      background: '#ffffff',
                      color: '#374151',
                      fontSize: '11px',
                      fontWeight: '700',
                      cursor: 'pointer',
                      outline: 'none',
                      appearance: 'none',
                      backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 24 24' stroke='%236b7280'%3E%3Cpath stroke-linecap='round' stroke-linejoin='round' stroke-width='2' d='M19 9l-7 7-7-7'/%3E%3C/svg%3E")`,
                      backgroundRepeat: 'no-repeat',
                      backgroundPosition: 'right 8px center',
                      backgroundSize: '16px'
                    }}
                  >
                    <option value="date">Sort: Date</option>
                    <option value="points">Sort: Points</option>
                    <option value="status">Sort: Status</option>
                  </select>
                </div>
              </div>
            </div>

            {/* History List */}
            {(() => {
              if (pointsWithdrawalRequests.length === 0) {
                return (
                  <div style={{
                    textAlign: 'center',
                    padding: '40px 20px',
                    color: '#6b7280'
                  }}>
                    <div style={{ fontSize: '48px', marginBottom: '16px' }}>📭</div>
                    <h3 style={{ fontSize: '18px', fontWeight: '700', color: '#374151', marginBottom: '8px' }}>
                      No Withdrawal History
                    </h3>
                    <p style={{ fontSize: '14px', color: '#6b7280' }}>
                      You haven't made any points withdrawal requests yet.
                    </p>
                  </div>
                );
              }

              if (filteredHistory.length === 0) {
                return (
                  <div style={{
                    textAlign: 'center',
                    padding: '40px 20px',
                    color: '#6b7280'
                  }}>
                    <div style={{ fontSize: '48px', marginBottom: '16px' }}>🔍</div>
                    <h3 style={{ fontSize: '18px', fontWeight: '700', color: '#374151', marginBottom: '8px' }}>
                      No Results Found
                    </h3>
                    <p style={{ fontSize: '14px', color: '#6b7280' }}>
                      Try adjusting your filters or search terms.
                    </p>
                  </div>
                );
              }
              
              return (
              <div style={{
                display: 'flex',
                flexDirection: 'column',
                gap: '12px',
                maxHeight: 'calc(95vh - 450px)',
                overflowY: 'auto',
                paddingRight: '4px'
              }}>
                {filteredHistory.map((request: any) => {
                    const requestDate = request.createdAt?.toDate ? request.createdAt.toDate() : new Date(request.createdAt);
                    const processedDate = request.processedAt?.toDate ? request.processedAt.toDate() : (request.processedAt ? new Date(request.processedAt) : null);
                    const rejectedDate = request.rejectedAt?.toDate ? request.rejectedAt.toDate() : (request.rejectedAt ? new Date(request.rejectedAt) : null);
                    
                    const isPending = request.status === 'pending';
                    const isApproved = request.status === 'approved';
                    const isRejected = request.status === 'rejected';
                    
                    const statusColor = isPending 
                      ? '#f59e0b' 
                      : isApproved 
                      ? '#10b981' 
                      : '#ef4444';
                    
                    const statusBg = isPending 
                      ? '#fef3c7' 
                      : isApproved 
                      ? '#d1fae5' 
                      : '#fee2e2';
                    
                    const statusIcon = isPending 
                      ? <ClockIcon style={{ width: '18px', height: '18px' }} />
                      : isApproved 
                      ? <CheckCircleIcon style={{ width: '18px', height: '18px' }} />
                      : <XCircleIcon style={{ width: '18px', height: '18px' }} />;

                    const formatDate = (date: Date) => {
                      return new Intl.DateTimeFormat('en-IN', {
                        dateStyle: 'short',
                        timeStyle: 'short'
                      }).format(date);
                    };

                    const formatRelativeTime = (date: Date) => {
                      const now = new Date();
                      const diffMs = now.getTime() - date.getTime();
                      const diffMins = Math.floor(diffMs / 60000);
                      const diffHours = Math.floor(diffMs / 3600000);
                      const diffDays = Math.floor(diffMs / 86400000);

                      if (diffMins < 1) return 'Just now';
                      if (diffMins < 60) return `${diffMins} min${diffMins !== 1 ? 's' : ''} ago`;
                      if (diffHours < 24) return `${diffHours} hour${diffHours !== 1 ? 's' : ''} ago`;
                      if (diffDays < 7) return `${diffDays} day${diffDays !== 1 ? 's' : ''} ago`;
                      return formatDate(date);
                    };

                    return (
                      <div
                        key={request.id}
                        style={{
                          backgroundColor: '#ffffff',
                          borderRadius: '16px',
                          padding: '16px',
                          border: `2px solid ${statusColor}20`,
                          boxShadow: '0 2px 8px rgba(0, 0, 0, 0.08)',
                          transition: 'all 0.2s ease'
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.transform = 'translateY(-2px)';
                          e.currentTarget.style.boxShadow = '0 4px 12px rgba(0, 0, 0, 0.12)';
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.transform = 'translateY(0)';
                          e.currentTarget.style.boxShadow = '0 2px 8px rgba(0, 0, 0, 0.08)';
                        }}
                      >
                        {/* Header Row */}
                        <div style={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'flex-start',
                          marginBottom: '12px'
                        }}>
                          <div style={{ flex: 1 }}>
                            <div style={{
                              fontSize: '24px',
                              fontWeight: '900',
                              color: '#111827',
                              marginBottom: '6px',
                              display: 'flex',
                              alignItems: 'center',
                              gap: '4px'
                            }}>
                              <span>₽</span>
                              <span>{request.points?.toLocaleString('en-IN') || 0}</span>
                            </div>
                            <div style={{
                              fontSize: '11px',
                              color: '#9ca3af',
                              fontFamily: 'monospace',
                              marginBottom: '4px',
                              letterSpacing: '0.5px'
                            }}>
                              ID: {request.id?.substring(0, 8) || 'N/A'}...
                            </div>
                            <div style={{
                              fontSize: '12px',
                              color: '#6b7280',
                              display: 'flex',
                              alignItems: 'center',
                              gap: '6px',
                              flexWrap: 'wrap'
                            }}>
                              <CalendarDaysIcon style={{ width: '14px', height: '14px' }} />
                              <span>{formatDate(requestDate)}</span>
                              <span style={{ color: '#d1d5db' }}>•</span>
                              <span style={{ fontSize: '11px', color: '#9ca3af' }}>
                                {formatRelativeTime(requestDate)}
                              </span>
                            </div>
                          </div>
                          <div style={{
                            padding: '8px 14px',
                            borderRadius: '20px',
                            backgroundColor: statusBg,
                            color: statusColor,
                            fontSize: '11px',
                            fontWeight: '800',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '6px',
                            textTransform: 'uppercase',
                            letterSpacing: '0.5px',
                            boxShadow: `0 2px 8px ${statusColor}30`
                          }}>
                            {statusIcon}
                            {request.status}
                          </div>
                        </div>

                        {/* Status Details */}
                        {isPending && (
                          <div style={{
                            padding: '10px',
                            backgroundColor: '#fffbeb',
                            borderRadius: '8px',
                            fontSize: '13px',
                            color: '#92400e',
                            lineHeight: '1.6'
                          }}>
                            Your request is pending admin approval. You will be notified once it's processed.
                          </div>
                        )}

                        {isApproved && processedDate && (
                          <div style={{
                            padding: '10px',
                            backgroundColor: '#f0fdf4',
                            borderRadius: '8px',
                            fontSize: '13px',
                            color: '#166534',
                            lineHeight: '1.6'
                          }}>
                            <strong>Approved on:</strong> {formatDate(processedDate)}
                            <br />
                            Your gift will be delivered soon. You will be contacted by company representatives.
                          </div>
                        )}

                        {isRejected && (
                          <div style={{
                            padding: '10px',
                            backgroundColor: '#fef2f2',
                            borderRadius: '8px',
                            fontSize: '13px',
                            color: '#991b1b',
                            lineHeight: '1.6'
                          }}>
                            {rejectedDate && (
                              <>
                                <strong>Rejected on:</strong> {formatDate(rejectedDate)}
                                <br />
                              </>
                            )}
                            {request.rejectReason ? (
                              <>
                                <strong>Reason:</strong> {request.rejectReason}
                              </>
                            ) : (
                              'This request was rejected. Please contact support for more information.'
                            )}
                          </div>
                        )}
                      </div>
                    );
                  })}
              </div>
              );
            })()}
          </div>
        </div>
      )}

      {/* Points Withdrawal Confirmation Popup */}
      {showPointsConfirmPopup && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.85)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 10000,
          padding: '20px'
        }}>
          <div style={{
            backgroundColor: 'white',
            borderRadius: '24px',
            padding: '28px',
            maxWidth: 'min(380px, calc(100vw - 32px))',
            width: '100%',
            boxShadow: '0 20px 60px rgba(0, 0, 0, 0.4)'
          }}>
            <div style={{ textAlign: 'center', marginBottom: '20px' }}>
              <div style={{
                width: '64px',
                height: '64px',
                borderRadius: '50%',
                background: 'linear-gradient(135deg, #f97316 0%, #fb923c 100%)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                margin: '0 auto 16px',
                boxShadow: '0 4px 12px rgba(249, 115, 22, 0.3)'
              }}>
                <span style={{ fontSize: '32px' }}>🎁</span>
              </div>
              <h3 style={{
                fontSize: '20px',
                fontWeight: '800',
                color: '#1a1a1a',
                marginBottom: '8px'
              }}>
                Confirm Points Withdrawal
              </h3>
              <p style={{
                fontSize: '14px',
                color: '#666',
                marginBottom: '20px'
              }}>
                You are requesting to withdraw:
              </p>
              <div style={{
                fontSize: '36px',
                fontWeight: '900',
                background: 'linear-gradient(135deg, #f97316 0%, #fb923c 100%)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                backgroundClip: 'text',
                marginBottom: '20px'
              }}>
                ₽{parseInt(pointsAmount).toLocaleString('en-IN')}
              </div>
            </div>

            <div style={{
              display: 'flex',
              gap: '12px'
            }}>
              <button
                onClick={() => {
                  setShowPointsConfirmPopup(false);
                }}
                style={{
                  flex: 1,
                  backgroundColor: '#f3f4f6',
                  color: '#4b5563',
                  border: 'none',
                  borderRadius: '16px',
                  padding: '14px',
                  fontSize: '15px',
                  fontWeight: '700',
                  cursor: 'pointer'
                }}
              >
                Cancel
              </button>
              <button
                onClick={async () => {
                  if (!user) return;
                  
                  setShowPointsConfirmPopup(false);
                  setPointsLoading(true);
                  
                  try {
                    const pointsValue = parseInt(pointsAmount);
                    
                    // Use Cloud Function for secure points withdrawal request creation
                    const { functions } = await import('../firebase');
                    const createPointsWithdrawalFunction = httpsCallable(functions, 'createPointsWithdrawalRequest');
                    const result = await createPointsWithdrawalFunction({ 
                      points: pointsValue
                    });
                    
                    const data = result.data as any;
                    
                    if (data.success) {
                      // Show success message
                      toast.success(`Points withdrawal request for ₽${pointsAmount} submitted successfully!`, {
                        duration: 4000,
                        style: {
                          background: '#f0fdf4',
                          color: '#15803d',
                          border: '2px solid #86efac',
                          fontSize: '15px',
                          fontWeight: '600'
                        }
                      });
                      
                      // Send notification
                      addNotification({
                        title: 'Points Withdrawal Request',
                        message: `Request for ₽${pointsAmount} points submitted successfully`,
                        type: 'success',
                        time: 'Just now'
                      });
                      
                      setPointsLoading(false);
                      setPointsAmount('');
                      setPointsErrorMessage('');
                      navigate('/payment');
                    } else {
                      // Backend returned success: false
                      const errorMessage = data.message || data.error || 'Failed to submit points withdrawal request';
                      console.error('❌ Points withdrawal request failed:', data);
                      
                      toast.error(`❌ ${errorMessage}`, {
                        duration: 4000,
                        style: {
                          background: '#fee2e2',
                          color: '#dc2626',
                          border: '2px solid #fca5a5',
                          fontSize: '15px',
                          fontWeight: '600'
                        }
                      });
                      setPointsLoading(false);
                    }
                  } catch (error: any) {
                    console.error('❌ Points withdrawal error:', error);
                    
                    // Extract error message from Firebase error
                    let errorMessage = 'Failed to submit points withdrawal request';
                    
                    if (error?.code) {
                      switch (error.code) {
                        case 'permission-denied':
                          errorMessage = 'Permission denied. Please contact support.';
                          break;
                        case 'unauthenticated':
                          errorMessage = 'Please log in again.';
                          break;
                        case 'invalid-argument':
                          errorMessage = error.message || 'Invalid points amount. Points must be in increments of 100 starting from 500.';
                          break;
                        case 'failed-precondition':
                          errorMessage = error.message || 'Request cannot be processed. Please check your points balance.';
                          break;
                        case 'resource-exhausted':
                          errorMessage = 'Rate limit exceeded. Please try again later.';
                          break;
                        default:
                          errorMessage = error.message || error.code || 'Failed to submit points withdrawal request';
                      }
                    } else if (error?.message) {
                      errorMessage = error.message;
                    }
                    
                    toast.error(`❌ ${errorMessage}`, {
                      duration: 4000,
                      style: {
                        background: '#fee2e2',
                        color: '#dc2626',
                        border: '2px solid #fca5a5',
                        fontSize: '15px',
                        fontWeight: '600'
                      }
                    });
                    setPointsLoading(false);
                  }
                }}
                style={{
                  flex: 1,
                  background: 'linear-gradient(135deg, #f97316 0%, #fb923c 100%)',
                  color: 'white',
                  border: 'none',
                  borderRadius: '16px',
                  padding: '14px',
                  fontSize: '15px',
                  fontWeight: '800',
                  cursor: 'pointer',
                  boxShadow: '0 4px 12px rgba(249, 115, 22, 0.4)'
                }}
              >
                Confirm
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
});

export default PointsRedeem;

